import {
  useCreateNangoIntegration,
  useIntegrations,
  useSyncKnowledgeAndWiki,
} from "@/api/integration/Integration.api";
import {
  Integration,
  IntegrationName,
  IntegrationType,
  isChatIntegrationName,
  isKnowledgeBaseIntegrationName,
  isNangoSupportedIntegrationName,
  isTaskManagementIntegrationName,
  isTicketSystemIntegrationName,
  supportedChat,
  supportedKnowledgeBase,
  supportedTaskManagement,
  supportedTicketSystems,
  existingTicketSystems,
  supportedWiki,
  isWikiIntegrationName,
  isIntegrationCompleted,
} from "@/api/integration/Integration.model";
import SelectIntegration from "./add-integration/select-integration/SelectIntegration";
import AddIntegrationDialog from "./add-integration/AddIntegration";
import { useCallback, useMemo, useState } from "react";
import { CTA } from "@/routes/onboarding/getting-started/GettingStarted";
import {
  Button,
  Callout,
  Flex,
  Grid,
  Link,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import { useIntegrationsTracking } from "./useIntegrationsTracking";
import {
  ExclamationTriangleIcon,
  UpdateIcon,
} from "@radix-ui/react-icons";
import { integrationNameToGalleryItem } from "@/api/integration/integrationNameToGalleryItem";

function IntegrationLoader() {
  return (
    <Flex pt={"35px"} px={"45px"} direction={"column"} gap={"36px"}>
      <Skeleton width={"240px"} height={"24px"} />

      <Grid
        columns="2"
        gap="3"
        display="inline-grid"
        width={"100%"}
        height={"fit-content"}
      >
        <Skeleton height={"109px"} style={{ borderRadius: "12px" }} />
        <Skeleton height={"109px"} style={{ borderRadius: "12px" }} />
        <Skeleton height={"109px"} style={{ borderRadius: "12px" }} />
      </Grid>
    </Flex>
  );
}

function NotAvailableIntegrationsHeader({
  integrations,
}: {
  integrations: Integration[];
}) {
  return (
    <div style={{ padding: "10px 45px 0" }}>
      <Callout.Root color="red">
        <Callout.Icon>
          <ExclamationTriangleIcon />
        </Callout.Icon>

        <Callout.Text>
          {integrations
            .map(
              (integration) =>
                integrationNameToGalleryItem[integration.name].title
            )
            .join(", ")}{" "}
          connection failed,{" "}
          <Link href="mailto:support@quack.ai">contact support</Link> for
          further assistance.{" "}
        </Callout.Text>
      </Callout.Root>
    </div>
  );
}

function SyncKnowledgeAndWikiButton({
  isSyncing,
  syncKnowledgeAndWiki,
}: {
  isSyncing: boolean;
  syncKnowledgeAndWiki: () => void;
}) {
  const handleSyncClick = useCallback(() => {
    syncKnowledgeAndWiki();
  }, [syncKnowledgeAndWiki]);

  return (
    <div style={{ padding: "10px 45px 0" }}>
      <Callout.Root>
        <Flex justify={"between"} width="100%" align={"center"} gap={"3"}>
          <Callout.Text>
            Manually sync resources from connected knowledge base and wiki
            integrations.
          </Callout.Text>
          <Button
            variant="outline"
            size="2"
            onClick={handleSyncClick}
            disabled={isSyncing}
          >
            {isSyncing ? (
              <Text size="2">Syncing…</Text>
            ) : (
              <>
                <UpdateIcon />
                Sync knowledge & wiki
              </>
            )}
          </Button>
        </Flex>
      </Callout.Root>
    </div>
  );
}

export function Integrations() {
  const { trackIntegrationViewed } = useIntegrationsTracking();
  const {
    data: integrations,
    isLoading: loadingIntegrations,
    refetch: reloadIntegrations,
  } = useIntegrations();

  const [integrationType, setIntegrationType] = useState<IntegrationType>();
  const [integrationName, setIntegrationName] = useState<IntegrationName>();

  const createNangoIntegration = useCreateNangoIntegration({
    reloadIntegrations,
  });
  const { mutateAsync: syncKnowledgeAndWiki, isLoading: isSyncing } =
    useSyncKnowledgeAndWiki();

  const handleKnowledgeBaseClick = useCallback((name: IntegrationName) => {
    setIntegrationType("KNOWLEDGE_BASE");
    setIntegrationName(name);
  }, []);

  const handleWikiClick = useCallback((name: IntegrationName) => {
    setIntegrationType("WIKI");
    setIntegrationName(name);
  }, []);

  const handleTaskManagementClick = useCallback((name: IntegrationName) => {
    setIntegrationType("TASK_MANAGEMENT");
    setIntegrationName(name);
  }, []);

  const handleTicketSystemClick = useCallback((name: IntegrationName) => {
    setIntegrationType("TICKET_SYSTEM");
    setIntegrationName(name);
  }, []);

  const handleChatClick = useCallback((name: IntegrationName) => {
    setIntegrationType("CHAT");
    setIntegrationName(name);
  }, []);

  const handleCreateIntegration = useCallback(
    (name: IntegrationName, type: IntegrationType) => {
      trackIntegrationViewed(type, name);

      if (type === "TICKET_SYSTEM" && isTicketSystemIntegrationName(name)) {
        if (supportedTicketSystems.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : handleTicketSystemClick(name);
        }
      } else if (
        type === "KNOWLEDGE_BASE" &&
        isKnowledgeBaseIntegrationName(name)
      ) {
        if (supportedKnowledgeBase.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : handleKnowledgeBaseClick(name);
        }
      } else if (type === "CHAT" && isChatIntegrationName(name)) {
        if (supportedChat.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : handleChatClick(name);
        }
      } else if (
        type === "TASK_MANAGEMENT" &&
        isTaskManagementIntegrationName(name)
      ) {
        if (supportedTaskManagement.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : null;
        }
      } else if (type === "WIKI" && isWikiIntegrationName(name)) {
        if (supportedWiki.includes(name)) {
          isNangoSupportedIntegrationName(name)
            ? createNangoIntegration({ name, type })
            : handleWikiClick(name);
        }
      }
    },
    [
      createNangoIntegration,
      handleKnowledgeBaseClick,
      handleTicketSystemClick,
      handleTaskManagementClick,
      handleChatClick,
    ]
  );

  const slackIntegrationExists = useMemo(
    () => integrations?.some((i) => i.type === "CHAT" && i.name==='slack') ?? false,
    [integrations]
  );

  const notAvailableIntegration = useMemo(() => {
    return integrations?.filter((i) => i.status === "NOT_AVAILABLE") ?? [];
  }, [integrations]);

  const existingIntegrations = useMemo(() => integrations ?? [], [integrations]);

  const knowledgeAndWikiIntegrations = useMemo(
    () =>
      existingIntegrations.filter(
        (i) =>
          (i.type === "KNOWLEDGE_BASE" || i.type === "WIKI") &&
          isIntegrationCompleted(i) &&
          i.id
      ),
    [existingIntegrations]
  );

  const zendeskTicketIntegrationExists = useMemo(
    () =>
      existingIntegrations.some(
        (i) =>
          i.name === "zendesk" &&
          i.type === "TICKET_SYSTEM" &&
          isIntegrationCompleted(i)
      ),
    [existingIntegrations]
  );

  const chatOptions = useMemo(() => {
    const chatIntegrations = supportedChat.filter(
      (option) => option !== "slack"
    );

    if (zendeskTicketIntegrationExists) {
      return chatIntegrations;
    }
    return chatIntegrations.filter((option) => option !== "smooch");
  }, [zendeskTicketIntegrationExists]);

  return (
    <Flex direction={"column"}>
      <Flex pt={"20px"} px={"45px"} direction={"column"}>
        <Text size={"5"} weight={"medium"} as={"div"}>
          Data Sources
        </Text>

        <Text size={"3"} color={"gray"} as={"div"}>
          Connect data sources to enable Quack to assist, analyze, and act.
        </Text>
      </Flex>

      {loadingIntegrations ? (
        <IntegrationLoader />
      ) : (
        <>
          {notAvailableIntegration.length > 0 && (
            <NotAvailableIntegrationsHeader
              integrations={notAvailableIntegration}
            />
          )}

          {knowledgeAndWikiIntegrations.length > 0 && (
              <SyncKnowledgeAndWikiButton isSyncing={isSyncing} syncKnowledgeAndWiki={syncKnowledgeAndWiki} />
          )}

          <SelectIntegration
            type="TICKET_SYSTEM"
            onClick={handleCreateIntegration}
            existingIntegrations={existingIntegrations}
            options={existingTicketSystems}
          />

          {chatOptions.length > 0 && (
            <SelectIntegration
              type="CHAT"
              onClick={handleCreateIntegration}
              existingIntegrations={existingIntegrations}
              options={chatOptions}
              isMultipleAllowed
            />
          )}

          <SelectIntegration
            type="KNOWLEDGE_BASE"
            onClick={handleCreateIntegration}
            existingIntegrations={existingIntegrations}
            options={supportedKnowledgeBase}
            isMultipleAllowed
          />

          <SelectIntegration
            type="WIKI"
            onClick={handleCreateIntegration}
            existingIntegrations={existingIntegrations}
            options={supportedWiki}
            isMultipleAllowed
          />

          <SelectIntegration
            type="TASK_MANAGEMENT"
            onClick={handleCreateIntegration}
            existingIntegrations={existingIntegrations}
            options={supportedTaskManagement}
          />

          <CTA
            slackIntegrated={slackIntegrationExists}
            reloadIntegrations={reloadIntegrations}
          />

          {integrationType && (
            <AddIntegrationDialog
              type={integrationType}
              preSelectedName={integrationName}
              loading={loadingIntegrations}
              existingIntegrations={existingIntegrations}
              onClose={() => setIntegrationType(undefined)}
              reloadIntegrations={reloadIntegrations}
            />
          )}
        </>
      )}
    </Flex>
  );
}
